import { Link } from 'react-router-dom'
import type { Project } from '../lib/projects'

const statusStyles = {
  active: { label: 'Active', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-400' },
  development: { label: 'In Development', bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-400' },
  planned: { label: 'Planned', bg: 'bg-slate-500/10', text: 'text-slate-500 dark:text-slate-400', dot: 'bg-slate-400' },
}

export default function ProjectCard({ project }: { project: Project }) {
  const status = statusStyles[project.status]

  return (
    <Link
      to={project.href}
      className="block group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden hover:border-slate-400 dark:hover:border-slate-500 transition-all hover:shadow-lg hover:shadow-slate-300/50 dark:hover:shadow-slate-900/50"
    >
      {/* Gradient header */}
      <div
        className="h-2"
        style={{
          background: `linear-gradient(to right, ${project.gradientFrom}, ${project.gradientTo})`,
        }}
      />

      <div className="p-6">
        {/* Icon + Title */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{project.icon}</span>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {project.name}
              </h3>
              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                {status.label}
              </div>
            </div>
          </div>
          <svg
            className="w-5 h-5 text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mt-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        {/* Description */}
        <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-4">
          {project.description}
        </p>

        {/* Features */}
        <ul className="space-y-1.5 mb-4">
          {project.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
              <svg className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        {/* Tech stack */}
        <div className="flex flex-wrap gap-1.5">
          {project.tech.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 bg-slate-200/50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 text-xs rounded-md"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Link>
  )
}
